import React, { useState, useMemo } from "react";
import { EntrySummary } from "../components/SpendAnalyzer";
import EntryTable from "../components/EntryTable";
import SegmentedControl from "../components/SegmentedControl";
import { calculateTotal, calculateTotalsByCategory } from "../utils/budgetUtils";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Anchored to the 1st so that setMonth() can't roll over from a day-of-month
// that the target month doesn't have (e.g. Jan 31 -> setMonth(1) -> Mar 3).
function getMonthName(monthNumber) {
  return new Date(2000, monthNumber - 1, 1).toLocaleString("en-US", {
    month: "long",
  });
}

const VIEW_OPTIONS = [
  { value: "received", label: "Received" },
  { value: "outstanding", label: "Outstanding" },
];

/**
 * The browse view for one entry kind: month navigator, per-category breakdown
 * and the list. Rendered twice — once for income, once for expenses.
 *
 * Income additionally gets a Received | Outstanding filter, because unpaid
 * receivables aren't month-scoped and so can't live under the month navigator.
 *
 * @param {Object} props
 * @param {import('../constants/entryKinds').EntryKind} props.kind
 * @param {string[]} props.categories
 * @param {Object[]} props.entries Month-scoped entries.
 * @param {Function} props.setEntries
 * @param {Object[]} [props.outstanding]
 * @param {Function} [props.setOutstanding]
 * @param {{ update: Function, remove: Function, markPaid?: Function }} props.api
 * @param {Function} props.onMutate
 */
const EntriesPage = ({
  kind,
  categories,
  entries,
  setEntries,
  outstanding = [],
  setOutstanding,
  month,
  year,
  setMonth,
  setYear,
  api,
  onMutate,
}) => {
  const [writeError, setWriteError] = useState("");
  const [view, setView] = useState("received");

  const showingOutstanding = kind.supportsOutstanding && view === "outstanding";

  const totalsByCategory = useMemo(
    () => calculateTotalsByCategory(entries),
    [entries]
  );

  const handlePreviousMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // Both lists are spliced locally so the row disappears immediately; onMutate
  // then re-syncs everything from the server.
  const removeLocally = (id) => {
    setEntries((current) => current.filter((entry) => entry._id !== id));
    setOutstanding?.((current) => current.filter((entry) => entry._id !== id));
  };

  const handleSave = async (editedEntry) => {
    setWriteError("");
    try {
      // The server response is the source of truth, and its dates stay in
      // YYYY-MM-DD — the same shape every other entry in state uses.
      const updated = await api.update(editedEntry);

      // Updated in place rather than removed, so an ordinary edit doesn't make
      // the row vanish and reappear. If the edit moved it out of the month
      // being browsed, onMutate's refetch corrects the list a moment later.
      setEntries((current) =>
        current.map((entry) => (entry._id === updated._id ? updated : entry))
      );
      // Outstanding is membership-based, not month-based, so it can be kept
      // exactly right here: setting a received date pays the entry off.
      setOutstanding?.((current) =>
        updated.date
          ? current.filter((entry) => entry._id !== updated._id)
          : current.map((entry) => (entry._id === updated._id ? updated : entry))
      );
      onMutate?.();
    } catch (error) {
      console.error("Error updating entry:", error);
      setWriteError(kind.saveErrorText);
    }
  };

  const handleDelete = async (id) => {
    setWriteError("");
    try {
      await api.remove(id);
      removeLocally(id);
      onMutate?.();
    } catch (error) {
      console.error("Error deleting entry:", error);
      setWriteError(kind.deleteErrorText);
    }
  };

  const handleMarkPaid = async (entry) => {
    setWriteError("");
    try {
      await api.markPaid(entry);
      removeLocally(entry._id);
      onMutate?.();
    } catch (error) {
      console.error("Error marking income paid:", error);
      setWriteError("Couldn't mark that as paid. Please try again.");
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen p-4">
      {showingOutstanding ? (
        <div className="flex items-baseline justify-between mb-4 px-2">
          <h3 className="text-2xl font-bold text-gray-800">Outstanding</h3>
          <p className="text-lg font-semibold text-gray-600">
            ${calculateTotal(outstanding).toFixed(2)}
          </p>
        </div>
      ) : (
        /* Month Navigator */
        <div className="flex items-center justify-between mb-4 px-2">
          <button
            onClick={handlePreviousMonth}
            aria-label="Previous month"
            className="p-3 rounded-xl bg-white hover:bg-gray-50 text-gray-600 shadow-sm border border-gray-200 transition-all active:scale-95"
          >
            <FaChevronLeft />
          </button>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800">
              {getMonthName(month)}
            </h3>
            <p className="text-sm text-gray-500">{year}</p>
          </div>
          <button
            onClick={handleNextMonth}
            aria-label="Next month"
            className="p-3 rounded-xl bg-white hover:bg-gray-50 text-gray-600 shadow-sm border border-gray-200 transition-all active:scale-95"
          >
            <FaChevronRight />
          </button>
        </div>
      )}

      {/* Below the month navigator: the month is the primary control on this
          page, and the filter reads as scoping what sits under it. */}
      {kind.supportsOutstanding && (
        <SegmentedControl
          value={view}
          onChange={setView}
          options={VIEW_OPTIONS}
          ariaLabel="Income view"
          className="mb-4"
        />
      )}

      {writeError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {writeError}
        </div>
      )}

      {showingOutstanding ? (
        <EntryTable
          entries={outstanding}
          categories={categories}
          kind={kind}
          mode="outstanding"
          onSave={handleSave}
          onDelete={handleDelete}
          onMarkPaid={handleMarkPaid}
        />
      ) : (
        <>
          <div className="mb-4">
            <EntrySummary
              totalsByCategory={totalsByCategory}
              entries={entries}
              kind={kind}
            />
          </div>
          <EntryTable
            entries={entries}
            categories={categories}
            kind={kind}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </>
      )}
    </div>
  );
};

export default EntriesPage;
